import { AsyncPipe, NgClass } from '@angular/common';
import { ChangeDetectionStrategy,Component, inject, OnDestroy, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatBadge } from '@angular/material/badge';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatMenu, MatMenuItem, MatMenuTrigger } from '@angular/material/menu';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatToolbar } from '@angular/material/toolbar';
import { ActivatedRoute, ActivatedRouteSnapshot, NavigationEnd, Router } from '@angular/router';
import { KeysResult, KeysResults } from 'fuzzysort';
import { Observable, Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';

import { FuseSearchBarComponent } from '@fuse/components/search-bar/search-bar.component';
import { FuseSidebarService } from '@fuse/components/sidebar/sidebar.service';
import { FuseConfigService } from '@fuse/services/config.service';
import { ViewContext, ViewContextService } from 'app/navigation/view-context.service';
import { AuthenticationService } from 'app/shared/authentication';
import { FuzzySortResultHighlightComponent } from 'app/shared/fuzzysort-helper/fuzzy-sort-result-highlight.component';
import { emptyFuzzySortOnKeys, executeFuzzySortOnKeys, KeysOfUnion } from 'app/shared/fuzzysort-helper/fuzzysort-helper';
import { AuthenticatedUser, StoreStatus } from 'app/shared/graph';
import { InteractDirective } from 'app/shared/utilities/interact.directive';
import { ShortStoreEnvPipe } from 'app/shared/utilities/short-store-env.pipe';

@Component({
    selector: 'toolbar', // eslint-disable-line
    templateUrl: './toolbar.component.html',
    styleUrls: ['./toolbar.component.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [MatToolbar, MatIconButton, MatIcon, MatButton, MatMenuTrigger, NgClass, MatMenu, MatMenuItem, FormsModule, FuseSearchBarComponent, AsyncPipe, ShortStoreEnvPipe, MatBadge, FuzzySortResultHighlightComponent, InteractDirective]
})
export class ToolbarComponent implements OnInit, OnDestroy {
    private readonly router = inject(Router);
    private readonly route = inject(ActivatedRoute);
    private readonly _fuseConfigService = inject(FuseConfigService);
    private readonly _fuseSidebarService = inject(FuseSidebarService);
    private readonly authenticationService = inject(AuthenticationService);
    private readonly snackBar = inject(MatSnackBar);
    viewContextService = inject(ViewContextService);

    @ViewChild(MatMenuTrigger) trigger: MatMenuTrigger;
    @ViewChild('storeMenu', { static: false }) storeMenu: MatMenu;

    horizontalNavbar: boolean;
    rightNavbar: boolean;
    hiddenNavbar: boolean;
    searchMode = false;
    _unsubscribeAll: Subject<any>;
    enrollingMfa = false;

    viewContext: ViewContext = { type: 'none' };
    availableContexts: ViewContext[];
    filteredContexts: KeysResults<ViewContext> = emptyFuzzySortOnKeys();
    contextFilterText: string = '';
    fuzzySearchKeys: Array<KeysOfUnion<ViewContext>> = ['storeId', 'storeLabel', 'subscriptionLabel', 'fulfillerId', 'libraryOwner'];

    userAccount$: Observable<AuthenticatedUser>;
    filter: {
        showActiveStores: boolean;
        showDeactivatedStores: boolean;
        showSubscriptions: boolean;
        showLibraries: boolean;
        showFulfillers: boolean;
    } = {
        showActiveStores: true,
        showDeactivatedStores: false,
        showSubscriptions: true,
        showLibraries: true,
        showFulfillers: true
    };
    someFilteredContextsHidden: boolean = false;
    currentRoute: ActivatedRouteSnapshot;

    constructor() {
        this.userAccount$ = this.authenticationService.authenticatedUser$;
        this._unsubscribeAll = new Subject();

        let savedFilter = localStorage.getItem('view-context-selector-filter');
        if (savedFilter) {
            this.filter = { ...this.filter, ...JSON.parse(savedFilter) };
        }
    }

    ngOnInit(): void {
        this.viewContextService.context$.pipe(takeUntil(this._unsubscribeAll)).subscribe(context => this.viewContext = context);
        this.viewContextService.availableContexts$.subscribe(contexts => {
            this.availableContexts = [...contexts];
            this.updateFilter();
        });

        // Subscribe to the config changes
        this._fuseConfigService.config.pipe(takeUntil(this._unsubscribeAll)).subscribe((settings: any) => {
            this.horizontalNavbar = settings.layout.navbar.position === 'top';
            this.rightNavbar = settings.layout.navbar.position === 'right';
            this.hiddenNavbar = settings.layout.navbar.hidden === true;
        });

        // The initial navigation has already ended when this runs, so seed the route
        // instead of waiting for the next NavigationEnd.
        this.currentRoute = this.deepestRoute();
        this.router.events.pipe(
            filter(event => event instanceof NavigationEnd),
            takeUntil(this._unsubscribeAll)
        ).subscribe(() => this.currentRoute = this.deepestRoute());
    }

    ngOnDestroy(): void {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next(undefined);
        this._unsubscribeAll.complete();
    }

    private deepestRoute(): ActivatedRouteSnapshot {
        let route = this.route;
        while (route.firstChild) {
            route = route.firstChild;
        }
        return route.snapshot;
    }

    toggleSidebarOpen(key: string): void {
        this._fuseSidebarService.getSidebar(key)!.toggleOpen();
    }

    searchCollapsedChanged(collapsed: boolean): void {
        this.searchMode = !collapsed;
    }

    async search(value: string): Promise<void> {
        if (!value) {
            return;
        }

        await this.router.navigate(['/search'], { queryParams: { q: value } });
    }

    selectFirstContext(event: Event): void {
        event.stopPropagation();
        if (this.filteredContexts.length > 0) {
            this.selectContext(this.filteredContexts[0].obj);
            setTimeout(() => this.trigger.closeMenu(), 0);
        }
    }

    selectContext(context: ViewContext): void {
        this.viewContextService.setContext(context);
        const routeData = this.currentRoute?.data ?? {};
        if (context.type === 'store') {
            if (routeData.viewContextType === context.type && routeData.viewContextChangeUrl) {
                this.router.navigateByUrl(routeData.viewContextChangeUrl.replace(':storeId', context.storeId));
            } else {
                this.router.navigate(['/stores', context.storeId, 'dashboards', 'overview']);
            }
        }
        if (context.type === 'library') {
            if (routeData.viewContextType === context.type && routeData.viewContextChangeUrl) {
                this.router.navigateByUrl(routeData.viewContextChangeUrl.replace(':libraryOwner', context.libraryOwner));
            } else {
                this.router.navigate(['/libraries', context.libraryOwner]);
            }
        }
        if (context.type === 'fulfiller') {
            if (routeData.viewContextType === context.type && routeData.viewContextChangeUrl) {
                this.router.navigateByUrl(routeData.viewContextChangeUrl.replace(':fulfillerId', context.fulfillerId));
            } else {
                this.router.navigate(['/fulfillers', context.fulfillerId]);
            }
        }
        if (context.type === 'subscription') {
            if (routeData.viewContextType === context.type && routeData.viewContextChangeUrl) {
                this.router.navigateByUrl(routeData.viewContextChangeUrl.replace(':subscriptionId', context.subscriptionId));
            } else {
                this.router.navigate(['/subscriptions', context.subscriptionId]);
            }
        }
    }

    signOut(): void {
        this.authenticationService.logout();
    }

    enrollMfa(): void {
        this.enrollingMfa = true;
        this.authenticationService.enrollMfa().subscribe({
            next: (result) => {
                if (result.alreadyEnabled) {
                    this.snackBar.open('MFA is already active');
                    this.enrollingMfa = false;
                    return;
                }
                window.location.href = result.url!;
            },
            error: (error) => {
                this.snackBar.open('An error occurred: ' + error.message, undefined, { duration: 4000 });
                this.enrollingMfa = false;
            }
        });
    }

    identifyContext(_index: number, context: KeysResult<ViewContext>): string {
        switch (context.obj.type) {
            case 'subscription':
                return 'sub:' + context.obj.subscriptionId;
            case 'none':
                return 'none';
            case 'store':
                return 'store:' + context.obj.storeId;
            case 'library':
                return 'library:' + context.obj.libraryOwner;
            case 'fulfiller':
                return 'fulfiller:' + context.obj.fulfillerId;
        }
    }

    preventMenuFocus($event: KeyboardEvent) {
        if ($event.key === 'ArrowDown' || $event.key === 'ArrowDown' || $event.key === 'Escape' || $event.key === 'Enter') {
            return;
        }
        $event.stopImmediatePropagation();
        this.updateFilter();
    }

    areContextEquals(context1: ViewContext, context2: ViewContext) {
        if (context1.type === 'store' && context2.type === 'store') {
            return context1.storeId === context2.storeId;
        }
        if (context1.type === 'subscription' && context2.type === 'subscription') {
            return context1.subscriptionId === context2.subscriptionId;
        }
        return false;
    }

    updateFilter() {
        let filterText = this.contextFilterText.toLowerCase();
        let viewableContexts = this.availableContexts.filter(c => {
            switch (c.type) {
                case 'none':
                    break;
                case 'store':
                    if (!this.filter.showActiveStores && !this.filter.showDeactivatedStores) {
                        return false;
                    }
                    return ((this.filter.showActiveStores && c.status == StoreStatus.Active) || (this.filter.showDeactivatedStores && c.status != StoreStatus.Active));
                case 'subscription':
                    return this.filter.showSubscriptions;

                case 'library':
                    return this.filter.showLibraries;

                case 'fulfiller':
                    return this.filter.showFulfillers;

            }
            return false;
        });

        let filteredContexts = executeFuzzySortOnKeys(
            filterText,
            viewableContexts,
            this.fuzzySearchKeys,
            25
        )

        this.someFilteredContextsHidden = filteredContexts.length >= 25;
        this.filteredContexts = filteredContexts;
        localStorage.setItem('view-context-selector-filter', JSON.stringify(this.filter));
    }

    toggleFilterShowActiveStores(event: MouseEvent) {
        event.stopPropagation();
        event.stopImmediatePropagation();
        event.preventDefault();
        this.filter.showActiveStores = !this.filter.showActiveStores;
        this.updateFilter();
    }

    toggleFilterShowDeactivatedStores(event: MouseEvent) {
        event.stopPropagation();
        event.stopImmediatePropagation();
        event.preventDefault();
        this.filter.showDeactivatedStores = !this.filter.showDeactivatedStores;
        this.updateFilter();
    }

    toggleFilterShowSubscriptions(event: MouseEvent) {
        event.stopPropagation();
        event.stopImmediatePropagation();
        event.preventDefault();
        this.filter.showSubscriptions = !this.filter.showSubscriptions;
        this.updateFilter();
    }

    toggleFilterShowLibraries(event: MouseEvent) {
        event.stopPropagation();
        event.stopImmediatePropagation();
        event.preventDefault();
        this.filter.showLibraries = !this.filter.showLibraries;
        this.updateFilter();
    }

    toggleFilterShowFulfillers(event: MouseEvent) {
        event.stopPropagation();
        event.stopImmediatePropagation();
        event.preventDefault();
        this.filter.showFulfillers = !this.filter.showFulfillers;
        this.updateFilter();
    }
}
